import {
  Checkbox,
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Currency, currenciesApi } from '@texas/api/endpoints/currenciesApi';
import {
  Country,
  countriesApi,
} from '@texas/api/endpoints/metadata/countriesApi';
import { VatCode, vatCodesApi } from '@texas/api/endpoints/vatCodesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { HttpClientResponse } from '@texas/api/httpClient/types';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';

export type BranchValueKind = 'countries' | 'currencies' | 'vatCodes';
export type BranchValue = Country | Currency | VatCode;

interface BranchValueForm {
  name: string;
  code: string;
  identifier: string;
  sellingRate: number;
  buyingRate: number;
  value: number;
  displayOrder: number;
  archived: boolean;
}

const emptyForm: BranchValueForm = {
  name: '',
  code: '',
  identifier: '',
  sellingRate: 1,
  buyingRate: 1,
  value: 0,
  displayOrder: 0,
  archived: false,
};

const titles: Record<BranchValueKind, string> = {
  countries: 'configuration.navigation.countries',
  currencies: 'configuration.navigation.currencies',
  vatCodes: 'configuration.navigation.vatCodes',
};

async function createValue(
  kind: BranchValueKind,
  branchId: number,
  form: BranchValueForm,
): Promise<HttpClientResponse<BranchValue>> {
  let response: HttpClientResponse<Country | Currency | VatCode>;
  switch (kind) {
    case 'countries':
      response = await countriesApi.create(branchId, {
        name: form.name,
        code: form.code,
        archived: form.archived,
      });
      break;
    case 'currencies':
      response = await currenciesApi.create(branchId, {
        name: form.name,
        code: form.code,
        sellingRate: form.sellingRate,
        buyingRate: form.buyingRate,
        archived: form.archived,
      });
      break;
    case 'vatCodes':
      response = await vatCodesApi.create(branchId, {
        name: form.name,
        identifier: form.identifier,
        value: form.value,
        displayOrder: form.displayOrder,
        archived: form.archived,
      });
      break;
  }
  return response as HttpClientResponse<BranchValue>;
}

async function updateValue(
  kind: BranchValueKind,
  id: number,
  form: BranchValueForm,
): Promise<HttpClientResponse<BranchValue>> {
  let response: HttpClientResponse<Country | Currency | VatCode>;
  switch (kind) {
    case 'countries':
      response = await countriesApi.update(id, {
        name: form.name,
        code: form.code,
        archived: form.archived,
      });
      break;
    case 'currencies':
      response = await currenciesApi.update(id, {
        name: form.name,
        code: form.code,
        sellingRate: form.sellingRate,
        buyingRate: form.buyingRate,
        archived: form.archived,
      });
      break;
    case 'vatCodes':
      response = await vatCodesApi.update(id, {
        name: form.name,
        identifier: form.identifier,
        value: form.value,
        displayOrder: form.displayOrder,
        archived: form.archived,
      });
      break;
  }
  return response as HttpClientResponse<BranchValue>;
}

export function BranchValueModal({
  branchId,
  isOpen,
  kind,
  onClose,
  onSaved,
  value,
}: {
  branchId: number;
  isOpen: boolean;
  kind: BranchValueKind;
  onClose: () => void;
  onSaved: () => void;
  value: BranchValue | null;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    request: create,
    loading: creating,
    error: createError,
  } = useApiRequest(createValue);
  const {
    request: update,
    loading: updating,
    error: updateError,
  } = useApiRequest(updateValue);
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<BranchValueForm>({ defaultValues: emptyForm });
  const title = t(titles[kind] as 'configuration.navigation.garp');

  useEffect(() => {
    reset(value ? { ...emptyForm, ...value } : emptyForm);
  }, [isOpen, reset, value]);

  const submit = async (form: BranchValueForm) => {
    const response = value
      ? await update(kind, value.id, form)
      : await create(kind, branchId, form);
    if (response.hasError) return;

    toast({
      title:
        value && form.archived !== value.archived
          ? t(
              form.archived
                ? 'general.entityArchived'
                : 'general.entityRestored',
              { name: form.name },
            )
          : value
          ? t('general.updated')
          : t('general.created'),
      status: 'success',
      isClosable: true,
    });
    onSaved();
    onClose();
  };

  const input = (
    name: keyof BranchValueForm,
    label: string,
    type: 'text' | 'number' = 'text',
  ) => (
    <FormControl isRequired={true} isInvalid={!!errors[name]}>
      <TexasFormLabel>{label}</TexasFormLabel>
      <Input
        type={type}
        step={
          name === 'buyingRate' || name === 'sellingRate' ? 'any' : undefined
        }
        variant="entityDirectory"
        {...register(name, {
          required: true,
          valueAsNumber: type === 'number',
          maxLength: type === 'text' ? 100 : undefined,
        })}
      />
      {type === 'text' && (
        <HookFormCounter control={control} name={name} maxLength={100} />
      )}
      <ErrorLabel text={errors[name]?.message} />
    </FormControl>
  );

  return (
    <Modal variant="entityDirectory" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          {value ? t('general.edit') : t('general.new')} {title.toLowerCase()}
        </ModalHeader>
        <form onSubmit={handleSubmit(submit)}>
          <ModalBody>
            <ErrorDetails error={createError ?? updateError} />
            <Flex direction="column" gap="4">
              {input('name', t('general.name'))}
              {kind !== 'vatCodes' && input('code', t('general.code'))}
              {kind === 'currencies' && (
                <>
                  {input('buyingRate', t('configuration.buyingRate'), 'number')}
                  {input(
                    'sellingRate',
                    t('configuration.sellingRate'),
                    'number',
                  )}
                </>
              )}
              {kind === 'vatCodes' && (
                <>
                  {input('identifier', t('configuration.identifier'))}
                  {input('value', t('general.value'), 'number')}
                  {input('displayOrder', t('branch.displayOrder'), 'number')}
                </>
              )}
              {value && (
                <Checkbox {...register('archived')}>
                  {t('general.archived')}
                </Checkbox>
              )}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <SubmitButton loading={creating || updating}>
              {t('general.save')}
            </SubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
